import { useState, useEffect } from 'react';
import { config } from '../config';
import { AudioMixer } from '../utils/audioMixer';
import './AudioSourceSelector.css';

export type AudioSourceType = 'microphone' | 'system' | 'both';

export interface AudioSourceConfig {
    type: AudioSourceType;
    microphoneDeviceId?: string;
    apiUrl: string;
    mixer?: AudioMixer;
}

interface AudioSourceSelectorProps {
    onSourceSelected: (config: AudioSourceConfig, stream: MediaStream) => void;
    onError: (error: string) => void;
}

interface MediaDeviceInfo {
    deviceId: string;
    label: string;
}

export default function AudioSourceSelector({ onSourceSelected, onError }: AudioSourceSelectorProps) {
    const [sourceType, setSourceType] = useState<AudioSourceType>('microphone');
    const [microphoneDevices, setMicrophoneDevices] = useState<MediaDeviceInfo[]>([]);
    const [selectedMicId, setSelectedMicId] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [browserSupport, setBrowserSupport] = useState({
        microphone: true,
        systemAudio: true,
    });

    const checkBrowserSupport = () => {
        const support = {
            microphone: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
            systemAudio: !!(navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia),
        };
        setBrowserSupport(support);

        if (!support.microphone) {
            onError('Your browser does not support microphone access. Please use a modern browser like Chrome, Firefox, or Safari.');
        }
    };

    const enumerateDevices = async () => {
        try {
            // First try to enumerate without permission (will show device IDs but not labels)
            const devices = await navigator.mediaDevices.enumerateDevices();
            const audioInputs = devices
                .filter(device => device.kind === 'audioinput')
                .map(device => ({
                    deviceId: device.deviceId,
                    label: device.label || `Microphone ${device.deviceId.slice(0, 5)}`,
                }));

            setMicrophoneDevices(audioInputs);
            if (audioInputs.length > 0 && !selectedMicId) {
                setSelectedMicId(audioInputs[0].deviceId);
            }

            // If we don't have labels, we'll get them after the user grants permission
            // when they click "Start Audio Capture"
        } catch (err) {
            console.error('Failed to enumerate devices:', err);
            // Don't show error here - we'll handle it when user tries to capture
        }
    };

    useEffect(() => {
        checkBrowserSupport();
        enumerateDevices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const captureMicrophone = async (deviceId?: string): Promise<MediaStream> => {
        const constraints: MediaStreamConstraints = {
            audio: deviceId && deviceId !== 'default' ? { deviceId: { ideal: deviceId } } : true,
            video: false,
        };

        try {
            const stream = await navigator.mediaDevices.getUserMedia(constraints);

            // After getting permission, re-enumerate devices to get proper labels
            const devices = await navigator.mediaDevices.enumerateDevices();
            const audioInputs = devices
                .filter(device => device.kind === 'audioinput')
                .map(device => ({
                    deviceId: device.deviceId,
                    label: device.label || `Microphone ${device.deviceId.slice(0, 5)}`,
                }));

            if (audioInputs.length > 0) {
                setMicrophoneDevices(audioInputs);
            }

            return stream;
        } catch (err) {
            if (err instanceof Error) {
                if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                    throw new Error('Microphone permission denied. Please allow microphone access and try again.');
                } else if (err.name === 'NotFoundError') {
                    throw new Error('No microphone found. Please connect a microphone and try again.');
                } else if (err.name === 'NotReadableError') {
                    throw new Error('Microphone is already in use by another application.');
                } else if (err.name === 'OverconstrainedError') {
                    // If the specific device constraint failed, try again with any microphone
                    return await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
                }
            }
            throw new Error('Failed to access microphone. Please check your device settings.');
        }
    };

    const captureSystemAudio = async (): Promise<MediaStream> => {
        if (!browserSupport.systemAudio) {
            throw new Error('System audio capture is not supported in your browser. Please use Chrome or Edge.');
        }

        try {
            // getDisplayMedia requires video to be true or both audio and video
            // We'll capture video but only use the audio track
            const stream = await navigator.mediaDevices.getDisplayMedia({
                audio: true,
                video: true,
            });

            // Check if audio track is present
            const audioTracks = stream.getAudioTracks();
            if (audioTracks.length === 0) {
                stream.getTracks().forEach(track => track.stop());
                throw new Error('No audio track available. Please select "Share audio" when prompted.');
            }

            // Stop video tracks since we only need audio
            const videoTracks = stream.getVideoTracks();
            videoTracks.forEach(track => track.stop());

            // Create a new stream with only audio tracks
            const audioOnlyStream = new MediaStream(audioTracks);
            return audioOnlyStream;
        } catch (err) {
            if (err instanceof Error) {
                if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                    throw new Error('Screen sharing permission denied. Please allow screen sharing to capture system audio.');
                } else if (err.message.includes('audio track')) {
                    throw err; // Re-throw our custom error
                }
            }
            throw new Error('Failed to capture system audio. Make sure to check "Share audio" in the dialog.');
        }
    };

    const handleStartCapture = async () => {
        setIsLoading(true);
        try {
            let stream: MediaStream;
            let mixer: AudioMixer | undefined;

            switch (sourceType) {
                case 'microphone':
                    stream = await captureMicrophone(selectedMicId);
                    break;

                case 'system':
                    stream = await captureSystemAudio();
                    break;

                case 'both': {
                    // Capture both streams
                    const micStream = await captureMicrophone(selectedMicId);
                    const sysStream = await captureSystemAudio();

                    // Create mixer and mix the streams
                    mixer = new AudioMixer();
                    stream = await mixer.initialize({
                        microphoneStream: micStream,
                        systemStream: sysStream,
                        microphoneGain: 0.5,
                        systemGain: 0.5,
                    });
                    break;
                }

                default:
                    throw new Error('Invalid audio source type');
            }

            const audioConfig: AudioSourceConfig = {
                type: sourceType,
                microphoneDeviceId: sourceType === 'microphone' || sourceType === 'both' ? selectedMicId : undefined,
                apiUrl: config.apiUrl,
                mixer,
            };

            onSourceSelected(audioConfig, stream);
        } catch (err) {
            if (err instanceof Error) {
                onError(err.message);
            } else {
                onError('An unknown error occurred while accessing audio devices.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="audio-source-selector">
            <h3>Select Audio Source</h3>

            {!browserSupport.microphone && (
                <div className="warning-message" role="alert">
                    ⚠️ Your browser does not support microphone access. Please use a modern browser.
                </div>
            )}

            {!browserSupport.systemAudio && sourceType !== 'microphone' && (
                <div className="warning-message" role="alert">
                    ⚠️ System audio capture is not supported in your browser. Try Chrome or Edge for this feature.
                </div>
            )}

            <div className="source-type-selector">
                <label>
                    <input
                        type="radio"
                        name="sourceType"
                        value="microphone"
                        checked={sourceType === 'microphone'}
                        onChange={(e) => setSourceType(e.target.value as AudioSourceType)}
                        disabled={!browserSupport.microphone}
                    />
                    <span>Microphone Only</span>
                </label>

                <label>
                    <input
                        type="radio"
                        name="sourceType"
                        value="system"
                        checked={sourceType === 'system'}
                        onChange={(e) => setSourceType(e.target.value as AudioSourceType)}
                        disabled={!browserSupport.systemAudio}
                    />
                    <span>System Audio Only</span>
                </label>

                <label>
                    <input
                        type="radio"
                        name="sourceType"
                        value="both"
                        checked={sourceType === 'both'}
                        onChange={(e) => setSourceType(e.target.value as AudioSourceType)}
                        disabled={!browserSupport.microphone || !browserSupport.systemAudio}
                    />
                    <span>Microphone + System Audio</span>
                </label>
            </div>

            {(sourceType === 'microphone' || sourceType === 'both') && microphoneDevices.length > 0 && (
                <div className="device-selector">
                    <label htmlFor="microphoneDevice">Select Microphone:</label>
                    <select
                        id="microphoneDevice"
                        value={selectedMicId}
                        onChange={(e) => setSelectedMicId(e.target.value)}
                        disabled={isLoading}
                    >
                        {microphoneDevices.map((device) => (
                            <option key={device.deviceId} value={device.deviceId}>
                                {device.label}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {sourceType === 'system' && (
                <div className="info-message">
                    <p>You will be prompted to select which window or screen to share.</p>
                    <p><strong>Important:</strong> Make sure to check "Share audio" in the dialog.</p>
                </div>
            )}

            {sourceType === 'both' && (
                <div className="info-message">
                    <p>You will capture both your microphone and system audio.</p>
                    <p>Audio mixing controls will be available after starting the stream.</p>
                </div>
            )}

            <button
                onClick={handleStartCapture}
                disabled={isLoading || !browserSupport.microphone}
                className="primary-button"
            >
                {isLoading ? 'Starting...' : 'Start Audio Capture'}
            </button>
        </div>
    );
}
